import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Typography,
} from '@mui/material';
import RenewDocuments from './RenewDocuments';
import RemoveVehicle from './RemoveVehicle';
import MarkCaptive from './MarkCaptive';
import { exportCsv } from './csv';
import useOperationalPermissions from '../common/util/operationalPermissions';
import brand from '../shell/brand';

const expired = (document) => new Date(`${document.expiresOn}T23:59:59`) < new Date();

// A fleet is a register, and a register is read by scanning down a column: which vehicles
// have expiring documents, which have no tracking attached. The card layout put every
// vehicle in its own block, which reads fine for three and not at all for three hundred.
const VehicleTable = ({
  rows: all, assignments = [], sources = [], documents = '', onClearDocuments, trips = [],
}) => {
  // A briefing line that names expiring paperwork opens the trucks it is about. Fortnight, because
  // that is the horizon the briefing itself warns on.
  const [page, setPage] = useState(0); const [pageSize, setPageSize] = useState(25);
  const [removing, setRemoving] = useState(null);
  const [marking, setMarking] = useState(null);
  const fortnight = Date.now() + 14 * 86400000;
  const papers = (vehicle) => vehicle.documents || [];
  const rows = documents === 'expiring'
    ? all.filter((vehicle) => papers(vehicle).some((doc) => expired(doc) || new Date(`${doc.expiresOn}T23:59:59`) < new Date(fortnight)))
    : all;
  // Everything the register knows about each truck, flattened into one row - including the
  // documents, because the reason somebody asks for this list is usually to find out whose
  // insurance is about to run out.
  const download = () => exportCsv(`${brand.key}-vehicles`, ['Vehicle', 'Type', 'Fleet', 'Hire', 'Owner', 'Transporter', 'Driver', 'Driver number', 'Tracking device', 'Device type', 'Device held', 'Documents', 'Expired documents'], rows.map((vehicle) => {
    const active = assignments.find((a) => a.vehicleId === vehicle.id && !a.endedAt);
    const source = active && sources.find((s) => s.id === active.sourceId);
    const documents = vehicle.documents || [];
    return {
      Vehicle: vehicle.name,
      Type: vehicle.vehicleType || '',
      // Named rather than left blank, because a reader of the sheet asking why one lorry has no
      // gate records needs the answer in the row, not in somebody's memory.
      Fleet: vehicle.fleet === 'CAPTIVE' ? 'Captive' : 'Gate traffic',
      Hire: vehicle.hire === 'MARKET' ? 'Market' : vehicle.hire === 'OWNED' ? 'Owned' : '',
      Owner: vehicle.owner || '',
      Transporter: vehicle.transporter || '',
      Driver: vehicle.driverName || '',
      'Driver number': vehicle.driverMobile || '',
      'Tracking device': source ? source.identifier : '',
      'Device type': active ? active.kind : '',
      'Device held': active ? (active.mode === 'PERMANENT' ? 'Fitted' : 'Issued for this trip') : '',
      Documents: documents.map((doc) => `${doc.kind} ${doc.reference} to ${doc.expiresOn}`).join(' | '),
      'Expired documents': documents.filter(expired).map((doc) => doc.kind).join(' | '),
    };
  }));
  // Renewing is done from the register itself: the column that reports an expiry is where somebody
  // is standing when the renewed paper arrives.
  const { operate, administer } = useOperationalPermissions();
  const [renewing, setRenewing] = useState(null);
  const attachment = (vehicleId) => {
    const active = assignments.find((a) => a.vehicleId === vehicleId && !a.endedAt);
    if (!active) return null;
    const source = sources.find((s) => s.id === active.sourceId);
    return { identifier: source?.identifier || `Source ${active.sourceId}`, kind: active.kind, mode: active.mode };
  };

  if (!rows.length) {
    return <Typography color="text.secondary" sx={{ p: 3 }}>No vehicles match this search.</Typography>;
  }

  return (
    <Stack gap={1.5}>
      {documents === 'expiring' && (
        <Alert
          severity="info"
          action={onClearDocuments && <Button size="small" onClick={onClearDocuments}>Show all vehicles</Button>}
        >
          {`Showing the ${rows.length} ${rows.length === 1 ? 'vehicle whose paperwork has' : 'vehicles whose paperwork has'} expired or expires within a fortnight.`}
        </Alert>
      )}
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography variant="body2" color="text.secondary">{`${rows.length} ${rows.length === 1 ? 'vehicle' : 'vehicles'}`}</Typography>
        <Button size="small" variant="outlined" sx={{ ml: 'auto' }} onClick={download}>Download CSV</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Vehicle', 'Type', 'Owner / transporter', 'Driver', 'Tracking', 'Documents', ''].map((h) => (
                <TableCell key={h || 'actions'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1)) * pageSize, (Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1)) + 1) * pageSize).map((row) => {
              const attached = attachment(row.id);
              const lapsed = (row.documents || []).filter(expired);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Stack direction="row" gap={1.5} alignItems="center">
                      {row.photoDataUrl && <Box component="img" src={row.photoDataUrl} alt="" sx={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 0.5 }} />}
                      <Typography fontWeight={600} variant="body2">{row.name}</Typography>
                      {/* The mark decides whether this lorry can raise a gate exception at all, so
                          it is on the row rather than behind a dialog - otherwise a fleet that
                          never appears in the exception list looks like a fault. */}
                      {row.fleet === 'CAPTIVE' && <Chip size="small" label="Captive" color="info" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.vehicleType || '—'}</TableCell>
                  <TableCell>{[row.owner, row.transporter].filter(Boolean).join(' · ') || '—'}</TableCell>
                  <TableCell>{[row.driverName, row.driverMobile].filter(Boolean).join(' · ') || '—'}</TableCell>
                  <TableCell>
                    {attached
                      ? <Chip size="small" label={`${attached.identifier} · ${attached.mode === 'PERMANENT' ? 'Permanent' : 'Temporary'}`} color="success" variant="outlined" />
                      : <Chip size="small" label="Not attached" variant="outlined" />}
                  </TableCell>
                  <TableCell>
                    {(row.documents || []).length === 0 ? '—' : (
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {/* Expiry is the reason anyone looks at this column, so an expired
                          document is coloured rather than left to be read out of a date. */}
                        {row.documents.map((d) => (
                          <Chip key={d.kind} size="small" color={expired(d) ? 'error' : 'default'} variant={expired(d) ? 'filled' : 'outlined'} label={`${d.kind} · ${d.expiresOn}`} />
                        ))}
                      </Stack>
                    )}
                    {lapsed.length > 0 && <Typography variant="caption" color="error" display="block">{`${lapsed.length} expired`}</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                      {operate && (
                      <Button size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => setRenewing(row)}>
                        {(row.documents || []).length ? 'Renew' : 'Add documents'}
                      </Button>
                      )}
                      {/* The undo for a spreadsheet loaded by mistake. A truck that has hauled
                          something is retired rather than erased, which the dialog says plainly. */}
                      {administer && (
                      <Button size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => setMarking(row)}>
                        {row.fleet === 'CAPTIVE' ? 'Gate traffic' : 'Mark captive'}
                      </Button>
                      )}
                      {administer && <Button size="small" color="error" onClick={() => setRemoving(row)}>Remove</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <RenewDocuments vehicle={renewing} onClose={() => setRenewing(null)} />
        {removing && <RemoveVehicle vehicle={removing} trips={trips} onClose={() => setRemoving(null)} />}
        {marking && <MarkCaptive vehicle={marking} onClose={() => setMarking(null)} />}
      </TableContainer>
      <TablePagination component="div" count={rows.length} page={Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1))} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} />
    </Stack>
  );
};

export default VehicleTable;
